import { filterCountries } from '@/utils/Helpers';
import { IonButton, IonIcon, IonItem, IonList, IonModal, IonSearchbar, IonTitle, IonToolbar } from '@ionic/react';
import { allCountries } from 'country-region-data';
import { chevronDown } from 'ionicons/icons';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type Region = {
  name: string;
  shortCode: string;
};

export type CountryRegion = {
  countryName: string;
  countryShortCode: string;
  regions: Region[];
};

type CountrySelectProps = {
  priorityOptions?: string[];
  whitelist?: string[];
  blacklist?: string[];
  defaultValue?: string;
  onChange?: (value: string) => void;
  className?: string;
  placeholder?: string;
};

// Create a memoized filtered countries list outside the component
const getFilteredCountries = (priorityOptions: string[] = [], whitelist: string[] = [], blacklist: string[] = []) => {
  // This will only be calculated once and reused
  return filterCountries(allCountries, priorityOptions, whitelist, blacklist);
};

function CountrySelect({
  priorityOptions = [],
  whitelist = [],
  blacklist = [],
  defaultValue,
  onChange = () => { },
  className,
  placeholder = 'Select Country',
}: CountrySelectProps) {
  // Initialize state without dependencies that could cause re-renders
  const [showModal, setShowModal] = useState(false);
  const [searchText, setSearchText] = useState('');
  const modal = useRef<HTMLIonModalElement>(null);
  const { t } = useTranslation();

  // Get countries list without using useState or useEffect
  const countriesList = useRef(getFilteredCountries(priorityOptions, whitelist, blacklist)).current;

  // Filter countries based on search text
  const filteredCountries = searchText.length > 0
    ? countriesList.filter(([name]) => {
      const translatedName = t('Countries:' + name.replace(' ', '_'), name);
      return (
        name.toLowerCase().includes(searchText.toLowerCase()) ||
        translatedName.toLowerCase().includes(searchText.toLowerCase())
      );
    })
    : countriesList;

  const handleSelect = (countrySlug: string) => {
    onChange(countrySlug);
    setShowModal(false);
  };

  // Find selected country without triggering re-renders
  const selectedCountry = defaultValue
    ? countriesList.find(([_, slug]) => slug === defaultValue.toUpperCase())
    : undefined;

  return (
    <>
      <IonButton
        fill='outline'
        onClick={() => setShowModal(true)}
        className={`w-full opacity-100 max-w-64 text-foreground ${className}`}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-4">
            {selectedCountry ? (
              <>
                <img
                  src={`/assets/images/flags/${selectedCountry[1]}.svg`}
                  alt={selectedCountry[0]}
                  className="w-8 h-6 object-cover rounded"
                />

                <span>{t('Countries:' + selectedCountry[0].replace(' ', '_'), selectedCountry[0])}</span>
              </>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <IonIcon icon={chevronDown} className="size-5" />
        </div>
      </IonButton>

      <IonModal
        ref={modal}
        isOpen={showModal}
        initialBreakpoint={0.75}
        breakpoints={[0, 0.75]}
        onDidDismiss={() => setShowModal(false)}
      >
        <IonToolbar className="pt-2 border-b">
          <IonTitle>{t('select_country')}</IonTitle>
        </IonToolbar >

        <IonSearchbar
          autoFocus
          value={searchText}
          onIonInput={(e) => setSearchText(e.detail.value || '')}
          placeholder={t('search_country')}
          className="mb-0"
        />

        <IonList lines='none' className="flex-1 overflow-y-auto">
          {filteredCountries.map(([countryName, countrySlug]) => (
            <IonItem
              key={countrySlug}
              button
              detail={false}
              onClick={() => handleSelect(countrySlug)}
            >
              <img
                src={`/assets/images/flags/${countrySlug}.svg`}
                alt={countryName}
                className="w-10 h-7 object-cover rounded mr-4"
              />
              {t('Countries:' + countryName.replace(' ', '_'), countryName)}
            </IonItem>
          ))}
        </IonList>
      </IonModal >
    </>
  );
}

export default CountrySelect;